import React, { useMemo, useRef, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import "./ReportedPlateClaim.scss";
import { Link } from "react-router-dom";
import {
  resolvePlate,
  revokePlateStatus,
  requestMoreInfo,
} from "../../../services/platesService";
import {
  getPlateDisplayImageUrl,
  getPlateLocationCountry,
  getPlateLocationState,
  getPlateNumber,
  getProfileUser,
  mapPlateStatusLabel,
} from "../../../utils/platesProfileHelpers";

function ReportedPlateClaim() {
  const location = useLocation();
  const queryClient = useQueryClient();
  const plateData = location.state?.plateData;
  const plateEntry = plateData?.plateEntry;
  const plateId =
    location.state?.plateId || plateEntry?._id || plateData?._id || plateData?.id;

  const resolvedModalRef = useRef(null);
  const revokeModalRef = useRef(null);
  const requestProofModalRef = useRef(null);

  const [revokeSubject, setRevokeSubject] = useState("");
  const [revokeMessage, setRevokeMessage] = useState("");
  const [requestProofMessage, setRequestProofMessage] = useState("");

  const revokeReasons = [
    "False Ownership",
    "Duplicate Claim",
    "Invalid Documentation",
    "Fraudulent Activity",
    "Other",
  ];

  const resolvePlateMutation = useMutation({
    mutationFn: (id) => resolvePlate(id),
    onSuccess: (data) => {
      toast.success(data?.message || "Report marked as resolved successfully", {
        position: "top-right",
        autoClose: 3000,
      });
      queryClient.invalidateQueries({ queryKey: ["plates"] });
      queryClient.invalidateQueries({ queryKey: ["plates-by-status"] });
      queryClient.invalidateQueries({ queryKey: ["plates", "reported"] });
    },
    onError: (error) => {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to mark report as resolved";
      toast.error(errorMessage, { position: "top-right", autoClose: 4000 });
    },
  });

  const revokePlateMutation = useMutation({
    mutationFn: ({ id, revokeData }) => revokePlateStatus(id, revokeData),
    onSuccess: (data) => {
      toast.success(data?.message || "Verification revoked successfully", {
        position: "top-right",
        autoClose: 3000,
      });
      setRevokeSubject("");
      setRevokeMessage("");
      queryClient.invalidateQueries({ queryKey: ["plates"] });
      queryClient.invalidateQueries({ queryKey: ["plates-by-status"] });
      queryClient.invalidateQueries({ queryKey: ["plates", "reported"] });
    },
    onError: (error) => {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to revoke verification";
      toast.error(errorMessage, { position: "top-right", autoClose: 4000 });
    },
  });

  const requestMoreInfoMutation = useMutation({
    mutationFn: ({ id, requestData }) => requestMoreInfo(id, requestData),
    onSuccess: (data) => {
      toast.success(data?.message || "Request sent successfully", {
        position: "top-right",
        autoClose: 3000,
      });
      setRequestProofMessage("");
      queryClient.invalidateQueries({ queryKey: ["plates"] });
      queryClient.invalidateQueries({ queryKey: ["plates-by-status"] });
      queryClient.invalidateQueries({ queryKey: ["plates", "reported"] });
    },
    onError: (error) => {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to send request";
      toast.error(errorMessage, { position: "top-right", autoClose: 4000 });
    },
  });

  const handleMarkAsResolved = () => {
    if (!plateId) {
      toast.error("Plate ID is missing");
      return;
    }
    resolvePlateMutation.mutate(plateId);
  };

  const handleRevokeVerification = () => {
    if (!plateId) {
      toast.error("Plate ID is missing");
      return;
    }
    if (!revokeSubject.trim()) {
      toast.error("Please select a reason", { position: "top-right", autoClose: 3000 });
      return;
    }
    if (!revokeMessage.trim()) {
      toast.error("Please enter a message", { position: "top-right", autoClose: 3000 });
      return;
    }
    revokePlateMutation.mutate({
      id: plateId,
      revokeData: {
        subject: revokeSubject.trim(),
        message: revokeMessage.trim(),
      },
    });
  };

  const handleRequestProof = () => {
    if (!plateId) {
      toast.error("Plate ID is missing");
      return;
    }
    if (!requestProofMessage.trim()) {
      toast.error("Please enter a message", { position: "top-right", autoClose: 3000 });
      return;
    }
    requestMoreInfoMutation.mutate({
      id: plateId,
      requestData: { message: requestProofMessage.trim() },
    });
  };

  useEffect(() => {
    const revokeEl = revokeModalRef.current;
    const requestEl = requestProofModalRef.current;
    const handleRevokeHidden = () => {
      setRevokeSubject("");
      setRevokeMessage("");
    };
    const handleRequestHidden = () => setRequestProofMessage("");
    if (revokeEl) revokeEl.addEventListener("hidden.bs.modal", handleRevokeHidden);
    if (requestEl) requestEl.addEventListener("hidden.bs.modal", handleRequestHidden);
    return () => {
      if (revokeEl) revokeEl.removeEventListener("hidden.bs.modal", handleRevokeHidden);
      if (requestEl) requestEl.removeEventListener("hidden.bs.modal", handleRequestHidden);
    };
  }, []);

  const displayData = useMemo(() => {
    if (!plateData) return null;

    const formatDate = (dateString) => {
      if (!dateString) return "N/A";
      try {
        const date = new Date(dateString);
        const dateStr = date.toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        });
        const timeStr = date.toLocaleTimeString("en-US", {
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        });
        return `${dateStr} | ${timeStr}`;
      } catch (e) {
        return dateString;
      }
    };

    const user = getProfileUser(plateData);
    const userName =
      plateData.businessName ||
      plateData.profilename ||
      plateData.username ||
      user.name ||
      user.fullName ||
      `${user.firstName || ""} ${user.lastName || ""}`.trim() ||
      user.email ||
      "N/A";
    const userId = user._id || user.id || user.username || "N/A";
    const plateNumber = getPlateNumber(plateEntry, plateData);

    const reportStatus = mapPlateStatusLabel(
      plateEntry?.status ?? plateData.status
    );

    const reportId =
      plateEntry?._id ||
      plateData.reportId ||
      plateData.report?.id ||
      plateData._id ||
      plateData.id ||
      "N/A";
    const reason =
      plateEntry?.reason ||
      plateData.reason ||
      plateData.reportReason ||
      plateData.report?.reason ||
      "N/A";
    const reportType =
      plateData.reportType ||
      plateData.report?.type ||
      "Ownership Dispute";

    return {
      reportId: String(reportId).length > 12 ? `RPT-${String(reportId).slice(-5)}` : reportId,
      reportIdRaw: reportId,
      sections: [
        {
          title: "Plate Information",
          items: [
            { label: "Plate Number", value: plateNumber },
            { label: "Report Status", value: reportStatus, type: "status" },
            {
              label: "Submission Date",
              value: formatDate(plateData.createdAt || plateData.reportedAt),
            },
            {
              label: "State / Region",
              value: getPlateLocationState(plateEntry, plateData) || "N/A",
            },
            {
              label: "Country",
              value: getPlateLocationCountry(plateEntry, plateData) || "N/A",
            },
            { label: "Report ID", value: reportId },
          ],
        },
        {
          title: "Media",
          items: [
            {
              label: "Plate Photo",
              image:
                getPlateDisplayImageUrl(plateEntry, plateData) ||
                process.env.PUBLIC_URL + "/images/plate.svg",
              type: "image",
            },
          ],
        },
        {
          title: "User Details",
          items: [
            { label: "Reported By", value: userName },
            { label: "User ID", value: userId },
            { label: "Report Type", value: reportType },
            {
              label: "Date Submitted",
              value: formatDate(plateData.createdAt || plateData.reportedAt),
            },
            { label: "Reason", value: reason },
          ],
        },
      ],
    };
  }, [plateData, plateEntry]);

  if (!plateData) {
    return (
      <React.Fragment>
        <header className="reported-plate-panel-header items-center gap-3 flex content-justify-between">
          <div className="left-side">
            <Link to="/plates-management/reported-plates">
              <img src={process.env.PUBLIC_URL + "/images/back.svg"} alt="back" />
            </Link>
            <h4 className="title">Plate Claim Review</h4>
          </div>
        </header>
        <div className="reportedPlateClaim">
          <div className="card">
            <p>No plate data available. Please select a report from the list.</p>
          </div>
        </div>
      </React.Fragment>
    );
  }

  return (
    <React.Fragment>
      <header className="reported-plate-panel-header items-center gap-3 flex content-justify-between">
        <div className="left-side">
          <Link to="/plates-management/reported-plates">
            <img src={process.env.PUBLIC_URL + "/images/back.svg"} alt="back" />
          </Link>
          <h4 className="title">Plate Claim Review</h4>
        </div>

        <div className="right-side">
          <button
            className="approved"
            data-bs-toggle="modal"
            data-bs-target="#resolvedModal"
          >
            Mark as Resolved
          </button>
          <button
            className="revoke"
            data-bs-toggle="modal"
            data-bs-target="#revokeVerifyModal"
          >
            Revoke Verification
          </button>
          <button
            className="info"
            data-bs-toggle="modal"
            data-bs-target="#temporaryModal"
          >
            Temporarily Hide Plate/Profile
          </button>
          <button
            className="suspend"
            data-bs-toggle="modal"
            data-bs-target="#requestProofModal"
          >
            Request Proof
          </button>
        </div>
      </header>

      <div className="report-id-wrapper">
        <div className="flex align-items-center justify-content-between">
          <p className="report-id-name">Report ID</p>
          <p className="report-id">{displayData.reportIdRaw}</p>
        </div>
      </div>

      <div className="reportedPlateClaim">
        <div className="row">
          {displayData.sections.map((section, index) => {
            const colSize = section.title === "Plate Information" ? "8" : section.title === "Media" ? "4" : "12";
            return (
              <div
                key={index}
                className={`col-lg-12 col-xl-${colSize}`}
              >
                <div className="card">
                  <h4>{section.title}</h4>

                  <div className="inner-content">
                    <div className="row">
                      {section.items.map((item, i) => (
                        <div
                          key={i}
                          className={`col-lg-4 col-xl-${section.title === "Media" ? "12" : "4"}`}
                        >
                          <div className="content-wrapper">
                            <img src={process.env.PUBLIC_URL + "/images/user-icon.svg"} alt="icon" />

                            <div className="content">
                              <h5>{item.label}</h5>

                              {item.type === "status" && (
                                <p
                                  className={`status-badge ${item.value?.toLowerCase() || "reported"}`}
                                >
                                  <span className="dot"></span> {item.value}
                                </p>
                              )}

                              {item.type === "status-approved" && (
                                <p className="status-badge approved">
                                  <span className="dot"></span> {item.value}
                                </p>
                              )}

                              {item.type === "image" && (
                                <div className="car-plate">
                                  <img
                                    src={item.image}
                                    alt="plate"
                                    onError={(e) => {
                                      e.target.src = process.env.PUBLIC_URL + "/images/plate.svg";
                                    }}
                                  />
                                </div>
                              )}

                              {!item.type && <p>{item.value}</p>}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Resolved Modal */}
      <div
        className="modal fade"
        id="resolvedModal"
        tabIndex="-1"
        aria-labelledby="resolvedModalLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id="resolvedModalLabel">
                Mark as Resolved
              </h5>
            </div>

            <div className="modal-body">
              <h3>Mark this report as resolved?</h3>
              <label className="form-label">
                This will close the case and move it to the Resolved list.
              </label>
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="close-btn"
                data-bs-dismiss="modal"
              >
                Close
              </button>

              <button
                type="button"
                className="green-btn"
                onClick={handleMarkAsResolved}
                disabled={resolvePlateMutation.isPending}
              >
                {resolvePlateMutation.isPending ? "Resolving..." : "Marked as Resolved"}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Revoke Modal */}
      <div
        ref={revokeModalRef}
        className="modal fade"
        id="revokeVerifyModal"
        tabIndex="-1"
        aria-labelledby="revokeVerifyModalLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id="revokeVerifyModalLabel">
                Revoke Verification
              </h5>
            </div>

            <div className="modal-body">
              <h3>
                This will remove the verified shield from the plate and notify the user.
              </h3>
              <label className="form-label">
                Are you sure you want to proceed?
              </label>
              <label className="form-label">Select Reason</label>

              {revokeReasons.map((reasonOption, index) => (
                <div key={index} className="form-check mb-2">
                  <input
                    type="radio"
                    name="revokeReason"
                    id={`revokeReason${index + 1}`}
                    checked={revokeSubject === reasonOption}
                    onChange={() => setRevokeSubject(reasonOption)}
                  />
                  <label className="form-check-label" htmlFor={`revokeReason${index + 1}`}>
                    {reasonOption}
                  </label>
                </div>
              ))}

              <label className="form-label">Message</label>
              <textarea
                className="form-control"
                rows="5"
                placeholder="Write here"
                value={revokeMessage}
                onChange={(e) => setRevokeMessage(e.target.value)}
              ></textarea>
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="close-btn"
                data-bs-dismiss="modal"
                disabled={revokePlateMutation.isPending}
              >
                Close
              </button>

              <button
                type="button"
                className="red-btn"
                onClick={handleRevokeVerification}
                disabled={revokePlateMutation.isPending}
              >
                {revokePlateMutation.isPending ? "Revoking..." : "Revoke Verification"}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Hide Plate Modal - UI only for now */}
      <div
        className="modal fade"
        id="temporaryModal"
        tabIndex="-1"
        aria-labelledby="temporaryModalLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id="temporaryModalLabel">
                Temporarily Hide Plate / Profile
              </h5>
            </div>

            <div className="modal-body">
              <h3>Hide this plate and the associated user profile?</h3>
              <label className="form-label">
                It will not be visible to others during review.
              </label>
              <br />
              <label className="form-label">Duration</label>
              <input
                type="text"
                className="form-control"
                placeholder="30 Days"
              />
              <label className="form-label">Internal Note</label>

              <textarea
                className="form-control"
                rows="3"
                placeholder="Write here"
              ></textarea>
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="close-btn"
                data-bs-dismiss="modal"
              >
                Close
              </button>

              <button type="button" className="info-btn">
                Temporarily Hide Plate/Profile
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Request Proof Modal */}
      <div
        ref={requestProofModalRef}
        className="modal fade"
        id="requestProofModal"
        tabIndex="-1"
        aria-labelledby="requestProofModalLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id="requestProofModalLabel">
                Request Proof
              </h5>
            </div>

            <div className="modal-body">
              <h3>Send message to user regarding this plate</h3>
              <label className="form-label">Message Box</label>
              <textarea
                className="form-control"
                rows="5"
                placeholder="Write here"
                value={requestProofMessage}
                onChange={(e) => setRequestProofMessage(e.target.value)}
              ></textarea>
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="close-btn"
                data-bs-dismiss="modal"
                disabled={requestMoreInfoMutation.isPending}
              >
                Close
              </button>

              <button
                type="button"
                className="submit-btn"
                onClick={handleRequestProof}
                disabled={requestMoreInfoMutation.isPending}
              >
                {requestMoreInfoMutation.isPending ? "Sending..." : "Request Proof"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </React.Fragment>
  );
}

export default ReportedPlateClaim;
